import { useEffect, useRef, useSyncExternalStore } from 'react';
import { useSearchParams } from 'react-router-dom';
import { shallow } from 'zustand/shallow';
import { AgentRegistryMembershipRole as Role } from '@tryiris-ai/common.protojs/api/v1/agent_registry';
import type { Room } from '@studio/models/room';
import { getActiveStudioId, subscribeActiveStudio } from '#~/analytics/active-studio.js';
import { useDepot } from '#~/depot/hooks.js';
import { useSettings } from '../../hooks.js';
import { Panel } from '../types.js';

/** Email links open the existing chooser only for the current Studio owner. */
export const BillingPlanLinkRoot = ({ roomId }: { readonly roomId: Room['id'] }): null => {
  const [params, setParams] = useSearchParams();
  const settings = useSettings();
  const activeStudio = useSyncExternalStore(subscribeActiveStudio, getActiveStudioId);
  const handled = useRef(false);
  const [actorID, role, ready] = useDepot((depot) => {
    const actor = depot.user?.id;
    const observation = depot.boot.observation;
    return [
      actor,
      depot.roomRecords[roomId]?.permissions.role,
      actor !== undefined &&
        observation?.accountId === actor &&
        observation.roomId === roomId &&
        observation.outcome === 'ready'
    ] as const;
  }, shallow);

  useEffect(() => {
    if (params.get('billing') !== 'plans') {
      handled.current = false;
      return;
    }
    // Unknown membership must settle for this account before consuming its link.
    if (handled.current || !actorID || !ready || role === undefined || activeStudio !== roomId) {
      return;
    }
    handled.current = true;
    if (role === Role.AGENT_REGISTRY_MEMBERSHIP_ROLE_OWNER) {
      settings(Panel.Billing, roomId, { openPlanPicker: true });
    }
    // A link never purchases a plan, and unrelated route options survive it.
    const next = new URLSearchParams(params);
    next.delete('billing');
    setParams(next, { replace: true });
  }, [actorID, activeStudio, params, ready, role, roomId, setParams, settings]);

  return null;
};
